// sectionSettings.ts
//
// Global Header & Footer are "static" sections that live inside the theme's
// `section_order` (entry type 'header' / 'footer'). Their per-section settings
// are NOT flattened by the worker, so theme components that read top-level
// `settings.*` keys would never see the live values.
//
// These helpers merge each section entry's `settings` into a top-level view so
// every theme (default + premium) reacts to Theme Editor edits in real time.

export interface SectionEntry {
  id: string;
  type: string;
  enabled?: boolean;
  settings?: Record<string, unknown>;
}

export function findSectionByType(settings: Record<string, unknown> | undefined, type: string): SectionEntry | null {
  const order = settings?.section_order;
  if (!Array.isArray(order)) return null;
  for (const s of order) {
    if (s && typeof s === 'object' && (s as { type?: string }).type === type) {
      return s as SectionEntry;
    }
  }
  return null;
}

export function getHeaderEntry(settings: Record<string, unknown> | undefined): SectionEntry | null {
  return findSectionByType(settings, 'header');
}

export function getFooterEntry(settings: Record<string, unknown> | undefined): SectionEntry | null {
  return findSectionByType(settings, 'footer');
}

/**
 * The Theme Settings tab (Universal Global Settings) declares master toggles
 * at the TOP LEVEL of the theme config (`upsell_cart_enabled`,
 * `mega_menu_enabled`, `footer_editor_enabled`). The header/footer SECTION
 * entries also carry `mega_menu_enabled` / `footer_editor_enabled` (default
 * false), which would clobber a top-level `true` after the merge below.
 *
 * To keep BOTH editing surfaces working, these switches are OR-merged last:
 * a `true` from the Settings tab OR the section entry wins. Also derives the
 * legacy `cart_upsell_enabled` key (read by the cart drawers) from the
 * universal `upsell_cart_enabled` so the value lands in React state — not just
 * in the DOM via applySettingsToDOM.
 */
function applyMasterSwitches(out: Record<string, unknown>, raw: Record<string, unknown> | undefined): void {
  const s = raw as Record<string, unknown> | undefined;
  if (!s) return;

  const anyTrue = (...vals: unknown[]): unknown => {
    const defined = vals.filter((v) => v !== undefined);
    if (defined.length === 0) return undefined;
    return defined.includes(true);
  };

  const mega = anyTrue(s.mega_menu_enabled, getHeaderEntry(s)?.settings?.mega_menu_enabled);
  if (mega !== undefined) out.mega_menu_enabled = mega;

  const footer = anyTrue(s.footer_editor_enabled, getFooterEntry(s)?.settings?.footer_editor_enabled);
  if (footer !== undefined) out.footer_editor_enabled = footer;

  if (s.upsell_cart_enabled !== undefined && out.cart_upsell_enabled === undefined) {
    out.cart_upsell_enabled = s.upsell_cart_enabled;
  }
}

/** Top-level settings + the header section entry overrides (for Header components). */
export function getHeaderSettings(settings: Record<string, unknown> | undefined): any {
  const s = settings as Record<string, unknown> | undefined;
  if (!s || typeof s !== 'object') return {};
  const flat: Record<string, unknown> = { ...s };
  const entry = getHeaderEntry(s);
  if (entry?.settings && typeof entry.settings === 'object') {
    for (const [k, v] of Object.entries(entry.settings)) {
      if (v !== undefined) flat[k] = v;
    }
  }
  applyMasterSwitches(flat, s);
  return flat;
}

/** Top-level settings + the footer section entry overrides (for Footer components). */
export function getFooterSettings(settings: Record<string, unknown> | undefined): any {
  const s = settings as Record<string, unknown> | undefined;
  if (!s || typeof s !== 'object') return {};
  const flat: Record<string, unknown> = { ...s };
  const entry = getFooterEntry(s);
  if (entry?.settings && typeof entry.settings === 'object') {
    for (const [k, v] of Object.entries(entry.settings)) {
      if (v !== undefined) flat[k] = v;
    }
  }
  applyMasterSwitches(flat, s);
  return flat;
}

/**
 * Universal announcement resolution — ONE source of truth for every theme's
 * announcement bar/ticker.
 *
 * As of the header-attachment refactor the Announcement Bar is NO LONGER an
 * addable/removable section. It is configured from the GLOBAL HEADER settings
 * (the `header_announcement_*` keys on the Header panel). Legacy stores that
 * still carry an `announcement` entry in section_order are supported via
 * fallback so their bars keep working until they hit the new toggle.
 *
 * Honors:
 *  - the global `header_announcement_enabled` toggle (default: on when text exists),
 *  - legacy `show_section_announcement` / `announcement_enabled` toggles,
 *  - the merged text (header keys take precedence over legacy keys).
 * Returns `{ text, settings, visible, link }`; `visible=false` when the toggle
 * is off or the text is empty — themes must NOT render a fallback string.
 */
export interface AnnouncementConfig {
  text: string;
  settings: Record<string, unknown>;
  visible: boolean;
  link: string;
}

export function getAnnouncement(settings: Record<string, unknown> | undefined): AnnouncementConfig {
  const s = settings as Record<string, unknown> | undefined;
  if (!s || typeof s !== 'object') return { text: '', settings: {}, visible: false, link: '' };

  const header = getHeaderSettings(s);
  const legacyEntry = getAnnouncementEntry(s);

  const source: Record<string, unknown> = { ...header };
  if (legacyEntry?.settings && typeof legacyEntry.settings === 'object') {
    for (const [k, v] of Object.entries(legacyEntry.settings)) {
      if (v !== undefined && source[k] === undefined) source[k] = v;
    }
  }

  const text = String(
    source.header_announcement_text
    ?? source.announcement
    ?? source.announcement_text
    ?? ''
  );
  const link = String(source.header_announcement_link ?? source.announcement_link ?? '');

  const sectionEnabled = legacyEntry ? legacyEntry.enabled !== false : true;
  const off = source.header_announcement_enabled === false
    || source.show_section_announcement === false
    || source.announcement_enabled === false;
  const visible = sectionEnabled && !off && text.trim().length > 0;

  // Normalize legacy `announcement_*` aliases so every existing theme read
  // (announcement.settings.announcement_bg, announcement_link, ...) keeps
  // working against the new global header keys.
  const merged: Record<string, unknown> = { ...source };
  if (text) { merged.announcement = text; merged.announcement_text = text; }
  if (source.header_announcement_link !== undefined) merged.announcement_link = link;
  if (source.header_announcement_bg !== undefined) merged.announcement_bg = source.header_announcement_bg;
  if (source.header_announcement_text_color !== undefined) merged.announcement_text_color = source.header_announcement_text_color;
  if (source.header_announcement_font_size !== undefined) merged.announcement_font_size = source.header_announcement_font_size;

  return { text, settings: merged, visible, link };
}

export function getAnnouncementEntry(settings: Record<string, unknown> | undefined): SectionEntry | null {
  return findSectionByType(settings, 'announcement');
}

/**
 * Product-page settings: top-level product_* keys merged with the per-page
 * instance settings (section_order `product_info` entry) so premium PDPs behave
 * EXACTLY like the shared one (single source of truth for gallery/countdown/
 * stock widgets/button radius).
 */
export function getProductPageSettings(settings: Record<string, unknown> | undefined): Record<string, unknown> {
  const s = settings as Record<string, unknown> | undefined;
  if (!s || typeof s !== 'object') return {};
  const flat: Record<string, unknown> = { ...s };
  const entry = findSectionByType(s, 'product_info');
  if (entry?.settings && typeof entry.settings === 'object') {
    for (const [k, v] of Object.entries(entry.settings)) {
      if (v !== undefined) flat[k] = v;
    }
  }
  if (s.instance_settings && typeof s.instance_settings === 'object') {
    const info = (s.instance_settings as Record<string, unknown>).product_info;
    if (info && typeof info === 'object') {
      for (const [k, v] of Object.entries(info as Record<string, unknown>)) {
        if (v !== undefined && flat[k] === undefined) flat[k] = v;
      }
    }
  }
  return flat;
}

/** Map a product_button_radius knob to a border-radius CSS value. */
export function productButtonRadius(settings: Record<string, unknown> | undefined): string {
  const radius = getProductPageSettings(settings).product_button_radius || 'none';
  switch (radius as string) {
    case 'small': return '4px';
    case 'medium': return '8px';
    case 'large': return '12px';
    case 'pill': return '9999px';
    case 'none':
    default: return '0px';
  }
}

/**
 * Header logo alignment style honoring `header_logo_position` (and the legacy
 * `logo_alignment`). Returns a style object every theme's wordmark/logo block
 * should spread so "Logo Position → left / center / right" works globally.
 * For flex-row headers, `marginLeft: 'auto'` on the logo item pushes it to the
 * far right of the navbar (flexbox free-space absorption).
 */
export function headerLogoAlign(settings: Record<string, unknown> | undefined): { justifyContent: string; textAlign: 'left' | 'center' | 'right'; marginLeft?: string } {
  const s = (settings || {}) as Record<string, unknown>;
  const align = (s.header_logo_position as string) || (s.logo_alignment as string) || 'left';
  if (align === 'right') return { justifyContent: 'flex-end', textAlign: 'right' as const, marginLeft: 'auto' };
  if (align === 'center') return { justifyContent: 'center', textAlign: 'center' as const };
  return { justifyContent: 'flex-start', textAlign: 'left' as const };
}

/**
 * Flatten the global Header + Footer section entries into the top level so
 * components reading `settings.logo_url`, `settings.mega_menu_*`,
 * `settings.footer_columns`, etc. see the live section values regardless of
 * which theme they belong to. Never mutates the input.
 */
export function flattenGlobalSections(settings: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!settings || typeof settings !== 'object') return {};
  const flat: Record<string, unknown> = { ...settings };
  for (const entry of [getHeaderEntry(settings), getFooterEntry(settings)]) {
    if (entry?.settings && typeof entry.settings === 'object') {
      for (const [k, v] of Object.entries(entry.settings)) {
        if (v !== undefined) flat[k] = v;
      }
    }
  }
  applyMasterSwitches(flat, settings);
  return flat;
}